import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { LinearProgress } from '@occc/ui-design-system';
import { Page, useAppLayoutContextConsumer } from '@occc-app/design-system';
import { useToggle } from '@occc/utils';
import { Cpg, ScanDataSftpCredentialStatus } from '@occc/graphql';

import { ScanDataSubmissionGroupsUpdate } from './scan-data-submission-groups-update.component';
import { buildBreadcrumbLinks } from './scan-data-submission-groups-update.constants';
import { ScanDataSubmissionGroupsUpdateFormValues } from './scan-data-submission-groups-update.types';
import { convertScanDataSubmissionGroupFormValuesToUpdateInput } from './convert-scan-data-submission-group-form-values-to-update-input';

import { useUpdateScanDataSftpCredentials } from '../../hooks/use-update-scan-data-sftp-credentials.hook';
import { useGetScanDataSftpCredentialByUuid } from '../../hooks/use-get-scan-data-sftp-credential-by-uuid.hook';

type SubmissionGroupsUpdatePageURLParams = {
  id: string;
};

export const ScanDataSubmissionGroupsUpdateController = () => {
  const { id } = useParams<SubmissionGroupsUpdatePageURLParams>();
  const uuid = id as string;

  const [showUpdateStatusModal, toggleUpdateStatusModal] = useToggle();
  const [isSftpTestSuccessful, setIsSftpTestSuccessful] = useState(false);
  const [isErrorHappened, setIsErrorHappened] = useState(false);
  const navigate = useNavigate();

  const { data, loading, error } = useGetScanDataSftpCredentialByUuid(uuid);
  const credential = data?.getScanDataSftpCredentialByUuid;

  const breadcrumbLinks = useMemo(
    () => buildBreadcrumbLinks(credential?.name || ''),
    [credential?.name],
  );
  useAppLayoutContextConsumer(breadcrumbLinks);

  const goBack = useCallback(() => navigate(-1), [navigate]);

  const onUpdateCompleted = useCallback(() => {
    setIsSftpTestSuccessful(true);
  }, []);

  const onUpdateError = useCallback(() => {
    setIsErrorHappened(true);
  }, []);

  const [updateScanDataSftpCredentials] = useUpdateScanDataSftpCredentials({
    refetchQueries: [
      'getScanDataSftpCredentialsOverview',
      'getScanDataSftpCredentialByUuid',
      'getScanDataSftpCredentialsWithStoreDetailsList',
    ],
    onCompleted: onUpdateCompleted,
    onError: onUpdateError,
  });

  const onSubmit = useCallback(
    (formValues: ScanDataSubmissionGroupsUpdateFormValues) => {
      toggleUpdateStatusModal();
      setIsErrorHappened(false);
      setIsSftpTestSuccessful(false);

      return updateScanDataSftpCredentials({
        variables: {
          input: convertScanDataSubmissionGroupFormValuesToUpdateInput(uuid, formValues),
        },
      });
    },
    [updateScanDataSftpCredentials, toggleUpdateStatusModal, uuid],
  );

  const onUpdateStatusModalCancel = useCallback(() => {
    toggleUpdateStatusModal();
    setIsErrorHappened(false);
    setIsSftpTestSuccessful(false);
  }, [toggleUpdateStatusModal]);

  const initialValues: ScanDataSubmissionGroupsUpdateFormValues = useMemo(
    () => ({
      name: credential?.name || '',
      tobaccoCompany: credential?.cpg || Cpg.Altria,
      submissionType:
        credential?.credentialStatus || ScanDataSftpCredentialStatus.ProductionSubmission,
      sftpDetails: {
        username: credential?.username || '',
        password: credential?.password || '',
        portNumber: credential?.port?.toString() || '',
        submissionURL: credential?.host || '',
        submitterID: credential?.submitterID || '',
      },
    }),
    [credential],
  );

  if (error) {
    return (
      <Page
        browserTabTitle='Error'
        pageHeaderProps={{ title: 'Error Loading Submission Group' }}
        containerProps={{ variant: 'md' }}
      >
        {error.message}
      </Page>
    );
  }

  if (loading || !credential) {
    return (
      <Page
        browserTabTitle='Loading...'
        pageHeaderProps={{ title: 'Loading...' }}
        containerProps={{ variant: 'md' }}
      >
        <LinearProgress />
      </Page>
    );
  }

  return (
    <Page
      pageHeaderProps={{
        title: 'Edit Submission Group',
        subtitle: credential?.name || '',
      }}
      browserTabTitle='Edit Submission Group'
    >
      <ScanDataSubmissionGroupsUpdate
        initialValues={initialValues}
        showUpdateStatusModal={showUpdateStatusModal}
        onUpdateStatusModalCancel={onUpdateStatusModalCancel}
        onSave={goBack}
        isSftpTestSuccessful={isSftpTestSuccessful}
        isErrorHappened={isErrorHappened}
        onSubmit={onSubmit}
        onCancel={goBack}
      />
    </Page>
  );
};
